import { withContactsAccess } from "@crm-src/components/page-permission-guard"

import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { BulkDeleteDialog } from "@/components/ui/bulk-delete-dialog"
import { EditContactDialog } from "@crm-src/components/contacts/edit-contact-dialog"
import { ViewContactDialog } from "@crm-src/components/contacts/view-contact-dialog"
import {
  UserPlus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Download,
  Upload,
  Eye,
  Mail,
  Link2,
} from "lucide-react"
import { ContactsAPI, type Contact, type ContactFormData, type ContactFilters } from "@crm-src/lib/contacts-api"
import { toggleId, addIdRange, collectIds } from "@crm-src/lib/selection"
import { INDUSTRY_OPTIONS, PROSPECT_INDUSTRIES, formatIndustry } from "@crm-src/lib/industries"
import { emailCampaignsPath } from "@crm-src/lib/email-campaign-routes"
import type { EmailRecipient, TargetSelection } from "@crm-src/components/email-target-selector"
import { useAppSelector } from "@crm-src/lib/store"
import { useUsers } from "@crm-src/hooks/useUsers"
import { useTableQuery } from "@crm-src/components/table/useTableQuery"
import { useDebouncedSearch } from "@crm-src/components/table/useDebouncedSearch"
import { useLatestRequest } from "@crm-src/components/table/useLatestRequest"
import { useTableSelection } from "@crm-src/components/table/useTableSelection"
import { buildTableParams } from "@crm-src/components/table/table-query"
import { SortableHeader } from "@crm-src/components/table/SortableHeader"
import { TableFilterBar } from "@crm-src/components/table/TableFilterBar"
import { TableEmptyState } from "@crm-src/components/table/TableEmptyState"
import { CONTACTS_TABLE } from "@crm-src/components/table/configs"
import type { TableColumn } from "@crm-src/components/table/configs"
import { buildContactFacetDefs } from "./contacts-page-query"
import type { ContactFacetKey } from "./contacts-page-query"

const PER_PAGE = 25

/**
 * The **Contacts** tab of CRM ▸ Contacts & Groups. `tabs` is the hub's tab
 * strip (`ContactsHubPage`), rendered directly under the shared page title so
 * the two tabs read as one page; the page still owns its own header actions.
 */
function ContactsPage({ tabs }: { tabs?: ReactNode } = {}) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const currentUser = useAppSelector(state => state.auth.user)
  // Fix: roles can be either string[] or object[] with name property
  const isAdmin = currentUser?.roles?.some(role => 
    typeof role === 'string' 
      ? (role === 'admin' || role === 'Admin')
      : (role.name === 'admin' || role.name === 'Admin')
  ) || currentUser?.is_super_admin === true
  
  // State management
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [showAllContacts, setShowAllContacts] = useState(false)

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Annotated, not inferred: TS 5.5 reads a `.filter(...)` callback as a type
  // predicate, so an inferred `facetKeys` would narrow to a different union
  // than the facet defs are built from. Contacts filters no key out today, but
  // the annotation is what keeps that true if it ever does.
  const facetKeys = useMemo<readonly ContactFacetKey[]>(() => CONTACTS_TABLE.facetKeys, [])
  const table = useTableQuery({
    defaultSort: CONTACTS_TABLE.defaultSort,
    defaultDir: CONTACTS_TABLE.defaultDir,
    facetKeys,
  })

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset, which
  // is the part that loops if a page writes it inline. Search text is
  // deliberately NOT in the URL; sort, facets and page are.
  const search = useDebouncedSearch(table.setPage)

  const { users } = useUsers()
  const facetDefs = useMemo(
    () => buildContactFacetDefs(PROSPECT_INDUSTRIES, users),
    [users],
  )
  // `as const` config entries carry no `align`, so the array needs the explicit
  // element type rather than the literal one TypeScript infers.
  const columns = useMemo<readonly TableColumn[]>(() => CONTACTS_TABLE.columns, [])

  // Pagination state (what the LAST response reported — the requested page
  // lives in the URL via `table.page`).
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: PER_PAGE
  })
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  
  // Bulk selection state
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false)
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)
  // Per-row actions menu anchor (keyed by contact id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: number } | null>(null)
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    surname: "",
    company: "",
    email: "",
    phone: "",
    mobile: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    job_title: "",
    notes: "",
    active: true,
    industry: "",
  })

  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL) and the search term from the
  // debounce above.
  //
  // `buildTableParams` is generic over the page's facet-key union, so the params
  // it returns type-check against `ContactFilters` field by field: a facet key
  // the API does not implement is a compile error here, not a param the server
  // silently drops. No cast.
  const listParams = useMemo<ContactFilters>(() => ({
    ...buildTableParams(
      { sort: table.sort, dir: table.dir },
      table.facets,
      table.page,
      PER_PAGE,
      search.search,
      // While a search is running and no column has been picked, this omits
      // `sort`/`dir` so the crm-api ranks by search relevance instead. See
      // buildTableParams.
      table.sortExplicit,
    ),
    show_all: isAdmin && showAllContacts,
  }), [table.sort, table.dir, table.sortExplicit, table.facets, table.page, search.search, isAdmin, showAllContacts])

  // Bulk selection, cleared whenever `listParams` describes a different result
  // set — otherwise "Delete 3" stays armed against rows a new filter has taken
  // off screen. Holds IDS rather than rows so the existing shift-click range
  // helpers (`toggleId` / `addIdRange` / `collectIds`, which speak `Set`) keep
  // working unchanged. See useTableSelection.
  const {
    selected: selectedIdList,
    setSelected: setSelectedIdList,
    clear: clearSelection,
  } = useTableSelection<number>(listParams)
  const selectedContactIds = useMemo(() => new Set(selectedIdList), [selectedIdList])
  const setSelectedContactIds = useCallback(
    (next: (prev: Set<number>) => Set<number>) =>
      setSelectedIdList([...next(new Set(selectedIdList))]),
    [selectedIdList, setSelectedIdList],
  )

  // Drops a slow earlier response that would otherwise land after a faster
  // later one — easy to hit now that a header or facet click fires immediately.
  // See useLatestRequest.
  const beginRequest = useLatestRequest()

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    const isCurrent = beginRequest()
    setLoading(true)
    try {
      const response = await ContactsAPI.getContacts(listParams)
      if (!isCurrent()) return
      setContacts(response.data)
      setPagination(response.meta?.pagination || {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: PER_PAGE
      })
    } catch (error) {
      if (!isCurrent()) return
      console.error("Failed to fetch contacts:", error)
      toast({
        title: "Error",
        description: "Failed to load contacts",
        variant: "destructive",
      })
      // Set empty data on error
      setContacts([])
      setPagination({
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: PER_PAGE
      })
    } finally {
      // Guarded: a superseded request must not clear the spinner belonging to
      // the request that superseded it.
      if (isCurrent()) setLoading(false)
    }
  }, [listParams, beginRequest, toast])

  // Load contacts on mount and when the query changes
  useEffect(() => {
    fetchContacts()
    // Reset the shift-click anchor when the rows underneath it change.
    setLastSelectedIndex(null)
  }, [fetchContacts])

  // Handle pagination
  const handlePageChange = (page: number) => {
    table.setPage(page)
  }

  // Handle create contact
  const handleCreateContact = async () => {
    // Validate at least name or email is provided
    if ((!formData.name || formData.name.trim() === "") && (!formData.email || formData.email.trim() === "")) {
      toast({
        title: "Validation Error",
        description: "Please provide at least a name or email for the contact.",
        variant: "destructive",
      })
      return
    }

    try {
      await ContactsAPI.createContact(formData)
      toast({
        title: "Success",
        description: "Contact created successfully",
      })
      setIsCreateDialogOpen(false)
      resetForm()
      fetchContacts()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      })
    }
  }

  // Handle update contact
  const handleUpdateContact = async () => {
    if (!selectedContact) return
    
    // Validate at least name or email is provided
    if ((!formData.name || formData.name.trim() === "") && (!formData.email || formData.email.trim() === "")) {
      toast({
        title: "Validation Error",
        description: "Please provide at least a name or email for the contact.",
        variant: "destructive",
      })
      return
    }

    try {
      await ContactsAPI.updateContact(selectedContact.id, formData)
      toast({
        title: "Success",
        description: "Contact updated successfully",
      })
      setIsEditDialogOpen(false)
      resetForm()
      fetchContacts()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update contact",
        variant: "destructive",
      })
    }
  }

  // Handle delete contact
  const handleDeleteContact = async () => {
    if (!selectedContact) return

    try {
      await ContactsAPI.deleteContact(selectedContact.id)
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      })
      setSelectedContact(null)
      fetchContacts()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      })
      throw error
    }
  }

  // Bulk selection handlers
  const handleSelectContact = (contactId: number, index: number, shiftKey: boolean = false) => {
    if (shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select the range between the anchor row and this one
      const anchor = lastSelectedIndex
      setSelectedContactIds(prev => addIdRange(prev, contacts, anchor, index, c => c.id))
    } else {
      // Normal click: toggle this single contact
      setSelectedContactIds(prev => toggleId(prev, contactId))
    }
    setLastSelectedIndex(index)
  }

  const handleSelectAllContacts = () => {
    if (selectedContactIds.size === contacts.length) {
      clearSelection()
    } else {
      setSelectedIdList([...collectIds(contacts, c => c.id)])
    }
  }

  const handleBulkDeleteContacts = () => {
    if (selectedContactIds.size === 0) return
    setIsBulkDeleteDialogOpen(true)
  }

  const confirmBulkDeleteContacts = async (ids: string[]) => {
    if (ids.length === 0) return

    try {
      const response = await ContactsAPI.bulkDeleteContacts(ids.map(id => Number(id)))
      toast({
        title: "Success",
        description: response.data.message,
      })
      clearSelection()
      fetchContacts()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete contacts",
        variant: "destructive",
      })
      throw error
    }
  }

  // Handle Excel/CSV import
  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const response = await ContactsAPI.importContacts(file)
      const { imported = 0, message } = response.data

      let description = message
      if (imported > 0) {
        description = `${imported} created`
      }
      
      toast({
        title: "Success",
        description: description || "Contacts processed successfully",
      })
      fetchContacts()
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to import contacts",
        variant: "destructive",
      })
    }
    event.target.value = "" // Reset file input
  }

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      await ContactsAPI.exportContacts(isAdmin && showAllContacts)
      toast({
        title: "Success",
        description: "Contacts exported to Excel successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export contacts",
        variant: "destructive",
      })
    }
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      surname: "",
      company: "",
      email: "",
      phone: "",
      mobile: "",
      address: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
      job_title: "",
      notes: "",
      active: true,
      industry: "",
    })
    setSelectedContact(null)
  }

  // Open edit dialog
  const openEditDialog = (contact: Contact) => {
    setSelectedContact(contact)
    setFormData({
      name: contact.name || "",
      surname: contact.surname || "",
      company: contact.company || "",
      email: contact.email || "",
      phone: contact.phone || "",
      mobile: contact.mobile || "",
      address: contact.address || "",
      city: contact.city || "",
      state: contact.state || "",
      postal_code: contact.postal_code || "",
      country: contact.country || "",
      job_title: contact.job_title || "",
      notes: contact.notes || "",
      active: contact.active,
      industry: contact.industry || "",
    })
    setIsEditDialogOpen(true)
  }

  // ── Email hand-off to the campaign composer ───────────────────────────────
  // Build a campaign from the current selection / industry filter and open the
  // composer with it pre-seeded (see EmailCampaignForm's location.state seed).
  const startCampaign = (initialTargetSelection: TargetSelection) => {
    navigate(emailCampaignsPath("/create"), { state: { initialTargetSelection } })
  }

  // Email the selected contacts (those with an email) as fixed recipients.
  const handleEmailSelected = () => {
    const recipients: EmailRecipient[] = contacts
      .filter(c => selectedContactIds.has(c.id) && c.email)
      .map(c => ({
        id: `contact_${c.id}`,
        name: c.full_name || c.email || "",
        email: c.email as string,
        type: "contact" as const,
        ...(c.company ? { company: c.company } : {}),
      }))
    if (recipients.length === 0) {
      toast({ title: "No emailable contacts", description: "None of the selected contacts have an email address.", variant: "destructive" })
      return
    }
    startCampaign({ recipients })
  }

  // Email EVERYONE in the active industry filter as a live segment (auto-includes
  // matching contacts not on the current page). The industry facet is
  // MULTI-select now, so the segment carries every selected label rather than
  // the single value the old dropdown held.
  const industryFilter = table.facets.industry
  const handleEmailIndustry = () => {
    if (industryFilter.length === 0) return
    startCampaign({ recipients: [], filters: { industries: [...industryFilter] } })
  }

  // Open delete dialog
  const openDeleteDialog = (contact: Contact) => {
    setSelectedContact(contact)
    setIsDeleteDialogOpen(true)
  }

  // Open view dialog
  const openViewDialog = (contact: Contact) => {
    setSelectedContact(contact)
    setIsViewDialogOpen(true)
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Contacts & Groups"
        description="Manage your contact directory and relationships"
        actions={
          <>
            {isAdmin && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, bgcolor: "surfaceSoft", borderRadius: 1, border: 1, borderColor: "divider" }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  labelPlacement="start"
                  control={
                    <Switch
                      id="show-all-contacts"
                      size="small"
                      checked={showAllContacts}
                      onChange={(e) => {
                        setShowAllContacts(e.target.checked)
                        table.setPage(1)
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.875rem", whiteSpace: "nowrap", mr: 1 }}>All Contacts</Typography>}
                />
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Download size={16} />}
              onClick={handleExportExcel}
            >
              Export
            </Button>
            <Button
              component="label"
              htmlFor="csv-upload"
              variant="outlined"
              color="inherit"
              startIcon={<Upload size={16} />}
            >
              Import
              <input
                id="csv-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={handleImportFile}
              />
            </Button>
            <Button
              variant="contained"
              startIcon={<UserPlus size={16} />}
              onClick={() => {
                resetForm()
                setIsCreateDialogOpen(true)
              }}
            >
              Add Contact
            </Button>
          </>
        }
      />

      {tabs}

      <Card>
        <CardHeader
          // The header now hosts a whole filter row, not one field: render it
          // as a <div> so the Boxes/Selects inside are not nested in a <span>.
          titleTypographyProps={{ component: "div" }}
          title={
            // The search box is WRAPPED, not replaced — same field, same 500ms
            // debounce; the facets simply sit beside it. The single-select
            // Industry dropdown that used to live here is now the multi-select
            // `industry` facet inside the bar.
            <TableFilterBar
              facetDefs={facetDefs}
              values={table.facets}
              onChange={table.setFacet}
              onClearAll={table.clearFacets}
              searchSlot={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", color: "text.secondary" }}><Search size={16} /></Box>
                  <TextField
                    placeholder="Search contacts..."
                    value={search.input}
                    onChange={(e) => search.setInput(e.target.value)}
                    size="small"
                    sx={{ width: { xs: "100%", sm: 340 } }}
                  />
                  {search.isSearching && (
                    <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                      Searching...
                    </Typography>
                  )}
                </Box>
              }
            />
          }
        />
        {(selectedContactIds.size > 0 || industryFilter.length > 0) && (
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider", bgcolor: "action.hover" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {selectedContactIds.size > 0 && (
                <>
                  <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                    {selectedContactIds.size} contact(s) selected
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Mail size={16} />}
                    onClick={handleEmailSelected}
                  >
                    Email Selected
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<Trash2 size={16} />}
                    onClick={handleBulkDeleteContacts}
                  >
                    Delete Selected
                  </Button>
                </>
              )}
              {industryFilter.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<Mail size={16} />}
                  onClick={handleEmailIndustry}
                  sx={{ ml: selectedContactIds.size > 0 ? "auto" : 0 }}
                >
                  {industryFilter.length === 1
                    ? `Email all in ${formatIndustry(industryFilter[0] as string)}`
                    : `Email all in ${industryFilter.length} industries`}
                </Button>
              )}
            </Box>
          </Box>
        )}
        <CardContent>
          {loading ? (
            <PageLoader />
          ) : contacts.length === 0 ? (
            // Names whichever narrowing actually emptied the list: filtering to
            // nothing used to claim the directory held no contacts at all.
            <TableEmptyState
              entityNoun="contacts"
              hasSearch={search.input.trim().length > 0}
              activeFilterCount={table.activeFilterCount}
              onClearFilters={table.clearFacets}
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>
                      <Checkbox
                        checked={selectedContactIds.size === contacts.length && contacts.length > 0}
                        onChange={handleSelectAllContacts}
                      />
                    </TableCell>
                    {/* The eight DATA columns, in `CONTACTS_TABLE` order —
                        which is the order the body cells below are written in,
                        so the two must be edited together. The checkbox and
                        actions cells either side stay plain <TableCell>s:
                        neither maps to a server column, so neither can sort. */}
                    {columns.map((col) => (
                      <SortableHeader
                        key={col.key}
                        column={col.key}
                        label={col.label}
                        sort={table.sort}
                        dir={table.dir}
                        onSort={table.toggleSort}
                        {...(col.align ? { align: col.align } : {})}
                      />
                    ))}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact, index) => (
                      <TableRow
                        key={contact.id}
                        hover
                      >
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSelectContact(contact.id, index, e.shiftKey)
                          }}
                        >
                          <Checkbox
                            checked={selectedContactIds.has(contact.id)}
                            onChange={() => {}}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 500, cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {contact.full_name || contact.email || `Contact #${contact.id}`}
                            {contact.source === "mirrored" && (
                              <Tooltip title={`Synced from ${contact.company || "a prospect/customer"} — managed there`}>
                                <Box component="span" sx={{ display: "inline-flex", color: "text.disabled" }}>
                                  <Link2 size={13} />
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          {contact.company && (
                            <Typography sx={{ fontWeight: 500, color: "text.primary" }}>{contact.company}</Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          {contact.industry && (
                            <Chip size="small" variant="outlined" label={formatIndustry(contact.industry)} sx={{ fontSize: "0.75rem" }} />
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          {contact.email && (
                            <Link
                              href={`mailto:${contact.email}`}
                              sx={{ color: "primary.main" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {contact.email}
                            </Link>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          {(contact.mobile || contact.phone) && (
                            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                              {contact.mobile || contact.phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          {contact.job_title && (
                            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>{contact.job_title}</Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          {contact.user ? (
                            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                              {contact.user.full_name}
                            </Typography>
                          ) : (
                            <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.disabled" }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ cursor: "pointer" }}
                          onClick={() => openViewDialog(contact)}
                        >
                          <Chip size="small" color={contact.active ? "primary" : "default"} label={contact.active ? "Active" : "Inactive"} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            sx={{ width: 32, height: 32 }}
                            aria-label="Open menu"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor({ el: e.currentTarget, id: contact.id })
                            }}
                          >
                            <MoreHorizontal size={16} />
                          </IconButton>
                          <Menu
                            anchorEl={menuAnchor?.id === contact.id ? menuAnchor.el : null}
                            open={menuAnchor?.id === contact.id}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                          >
                            <MenuItem onClick={() => {
                              setMenuAnchor(null)
                              openViewDialog(contact)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                              View Details
                            </MenuItem>
                            <MenuItem onClick={() => {
                              setMenuAnchor(null)
                              openEditDialog(contact)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                              Edit
                            </MenuItem>
                            <Divider />
                            <MenuItem
                              onClick={() => {
                                setMenuAnchor(null)
                                openDeleteDialog(contact)
                              }}
                              sx={{ color: "error.main" }}
                            >
                              <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                              Delete
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Pagination
            {...paginationPropsFromMeta(pagination)}
            entityNoun="contacts"
            hideIfSinglePage
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Create Contact Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Fill in the contact information below
          </DialogContentText>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                id="name"
                label="Name"
                size="small"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                id="surname"
                label="Surname"
                size="small"
                fullWidth
                value={formData.surname}
                onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
              />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                id="company"
                label="Company"
                size="small"
                fullWidth
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
              <TextField
                id="job_title"
                label="Job Title"
                size="small"
                fullWidth
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                id="email"
                label="Email"
                type="email"
                size="small"
                fullWidth
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <TextField
                id="phone"
                label="Phone"
                size="small"
                fullWidth
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                id="mobile"
                label="Mobile"
                size="small"
                fullWidth
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
              <TextField
                id="industry"
                label="Industry"
                select
                size="small"
                fullWidth
                value={formData.industry ?? ""}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {INDUSTRY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              id="address"
              label="Address"
              size="small"
              fullWidth
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
              <TextField
                id="city"
                label="City"
                size="small"
                fullWidth
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
              <TextField
                id="state"
                label="State"
                size="small"
                fullWidth
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
              <TextField
                id="postal_code"
                label="Postal Code"
                size="small"
                fullWidth
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              />
              <TextField
                id="country"
                label="Country"
                size="small"
                fullWidth
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Box>
            <TextField
              id="notes"
              label="Notes"
              multiline
              minRows={3}
              size="small"
              fullWidth
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setIsCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateContact}>Create Contact</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Dialog */}
      <EditContactDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdateContact}
        contact={selectedContact}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Confirm Delete"
        description={`Are you sure you want to delete ${selectedContact?.full_name || selectedContact?.email || 'this contact'}? This action cannot be undone.`}
        confirmLabel="Delete Contact"
        onConfirm={handleDeleteContact}
      />

      {/* View Contact Details Dialog */}
      <ViewContactDialog
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        contact={selectedContact}
        onEdit={openEditDialog}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <BulkDeleteDialog
        isOpen={isBulkDeleteDialogOpen}
        onClose={() => setIsBulkDeleteDialogOpen(false)}
        items={contacts.filter(c => selectedContactIds.has(c.id))}
        entityLabel={{ singular: "Contact", plural: "Contacts" }}
        cascadingWarning="The selected contacts will be permanently removed from your contact directory."
        renderItem={(c) => c.full_name || c.email || `Contact ${c.id}`}
        onConfirm={confirmBulkDeleteContacts}
      />
    </Box>
  )
}

export default withContactsAccess(ContactsPage)
